/**
 * UI metadata for Hub work-item types.
 *
 * Hub vocabulary (what the badge displays):
 *   - **feature** — everything that isn't one of the special
 *     buckets below. Stored as `gitlabType="feature"`.
 *   - **bug**      — a defect/error. Runs the EXACT SAME workflow as
 *     feature (full lifecycle — spec, estimate, dev/test/main); it's
 *     a Hub-only reporting label, not a different process. GitLab has
 *     no native "bug" concept, so it mirrors as a plain `issue` too.
 *     `gitlabType="bug"`.
 *   - **hotfix**   — a fix that must ship immediately, bypassing the
 *     normal dev/test chain. Genuinely rare by design — reserve it
 *     for "production is down right now", not everyday urgency.
 *     `gitlabType="hotfix"`.
 *   - **task**     — work that doesn't touch code.
 *     `gitlabType="task"`.
 *
 * "Issue" doesn't exist in Hub at all — that label belongs only to
 * the GitLab side (Hub's gitlab-sync worker translates GitLab
 * `issue` → Hub `feature` on read, unless the row is already a Hub
 * "bug" — see `resolveHubGitlabType` server-side, which this module
 * has no equivalent of since it only ever reads an already-resolved
 * value). Sub-tasks (a task nested under a parent) were a fifth type;
 * the whole parent/child concept has been removed — a work item can
 * no longer have a parent.
 *
 * Legacy keys (`issue`, `incident` as a raw gitlabType) are kept as
 * aliases so any row a rename migration didn't catch still renders
 * under the new label.
 */

export type WorkItemTypeInput =
  | string
  | null
  | undefined
  | {
      gitlabType?: string | null;
    };

type ResolvedType = "feature" | "bug" | "hotfix" | "task";

function resolve(input: WorkItemTypeInput): ResolvedType | null {
  if (input === null || input === undefined) return null;
  const t = (typeof input === "string" ? input : input.gitlabType)?.toLowerCase();
  if (!t) return null;
  // Legacy `issue` / `incident` are Hub-vocabulary aliases.
  if (t === "feature" || t === "issue") return "feature";
  if (t === "bug") return "bug";
  if (t === "hotfix" || t === "incident") return "hotfix";
  if (t === "task") return "task";
  return null;
}

const COLORS: Record<ResolvedType, string> = {
  feature: "#3b82f6",
  bug: "#f97316",
  hotfix: "#ef4444",
  task: "#8b5cf6",
};

const LABELS: Record<ResolvedType, string> = {
  feature: "FEATURE",
  bug: "BUG",
  hotfix: "HOTFIX",
  task: "TASK",
};

export function typeColor(input: WorkItemTypeInput): string {
  const r = resolve(input);
  return r ? COLORS[r] : "#6b7280";
}

export function typeLabel(input: WorkItemTypeInput): string {
  const r = resolve(input);
  return r ? LABELS[r] : "—";
}
